import React from 'react';
import classes from './RelatedRooms.module.scss';
import { Card, Empty, Tree, UploadFile } from 'antd';
import Dragger from 'antd/es/upload/Dragger';
import { useAppDispatch, useAppSelector } from 'store/hook';
import { useTranslation } from 'react-i18next';
import { CaretDownOutlined, LinkOutlined } from '@ant-design/icons';
import ModalRelatedRoom from './components/ModalRelatedRoom';
import api from 'api';
import { IDocument } from 'interfaces';
import Spinner from 'components/Spinner/Spinner';
import RelatedRoom from './components/RelatedRoom';
import cn from 'classnames';
import ModalRoomTerm from './components/ModalRoomTerm';
import ReferringRooms from './components/ReferringRooms';
import { AntTreeNodeProps, DataNode } from 'antd/es/tree';
import { setCurrentRelatedRoomTerms, setIsOpenAttachRoomModal, setNeedToUpdateTerms } from 'store/slices/dataRoomSlice';
import { addChildrenFunction, updateTreeData } from './helpers/updateTreeData';

type RelatedRoomsProps = {
  relatedRef: React.MutableRefObject<HTMLDivElement | null>;
};

const RelatedRooms = ({relatedRef}: RelatedRoomsProps) => {
  const [document, setDocument] = React.useState<UploadFile[]>([]);
  const [isLoadingRelatedRooms, setIsLoadingRelatedRooms] = React.useState(false);
  const [relatedRoomsTerms, setRelatedRoomsTerms] = React.useState<IDocument[]>([]);
  const [deletedRoomsTerms, setDeletedRoomsTerms] = React.useState<IDocument[]>([]);
  const [modalTermOpen, setModalTermOpen] = React.useState(false);
  const [selectedTerm, setSelectedTerm] = React.useState<IDocument | null>(null);
  const [referringRooms, setReferringRooms] = React.useState<IDocument[]>([]);

  const [treeData, setTreeData] = React.useState<DataNode[]>([]);
  const [needToUpdateTree, setNeedToUpdateTree] = React.useState(true);
  const [expandedKeys, setExpandedKeys] = React.useState<React.Key[]>([]);
  const [loadedKeys, setLoadedKeys] = React.useState<React.Key[]>([]);
  // const [autoExpandParent, setAutoExpandParent] = React.useState<boolean>(true);  

  const { userPermissions } = useAppSelector((state) => state.userData);
  const { dataRoom, isOpenAttachRoomModal, parentRoom, needToUpdateTerms } = useAppSelector((state) => state.dataRoom);
  const { messageApi } = useAppSelector((state) => state.windowState);

  const canEdit = userPermissions?.can_create_access_point;  
  
  const { t } = useTranslation();  
  const dispatch = useAppDispatch();
  
  const onExpand = (expandedKeysValue: React.Key[]) => {
    setExpandedKeys(expandedKeysValue);
    // setAutoExpandParent(false);
  };  
  
  const onLoadData = async ({ key, children }: any) => {
    setLoadedKeys(prev => [...prev, key]);

    const roomId = key.split('_')[0];
    const permissionsOfSelectedRoom = await api.getRoomsPermissions({room_id: roomId});
    const permission = permissionsOfSelectedRoom.data?.can_create_access_point;

    if (children) {
      setTreeData((origin) =>
        updateTreeData(origin, key, [], relatedRoomsTerms, permission),
      );
      return;
    };

    const { data: childrenFolders } = await api.getRelatedRooms({room_id: roomId!, can_create_access_point: canEdit}); 

    const activeChildren = childrenFolders.filter((room: IDocument) => room.related_room?.room_status === 'available')
    const childrenElements = activeChildren.map((term: any) => ({
      key: `${term.related_room!.id}_${term.id}_${key}`,
      isLeaf: (canEdit || term.user_status?.status_access === 'approved') ? false : true,
      title: <RelatedRoom
        key={term.id}
        document={term}
        setIsOpen={setModalTermOpen}
        setSelectedTerm={setSelectedTerm}
        setRelatedRoomsTerms={setRelatedRoomsTerms}
        setTreeData={setTreeData}
      />,
      // children: term.related_room?.has_childs ? undefined : []
    }));

    const addedChildren = relatedRoomsTerms.map(term => addChildrenFunction(term, key.split('_')[1], activeChildren));
    
    setRelatedRoomsTerms(addedChildren);
    setTreeData((origin) =>
      updateTreeData(origin, key, childrenElements, addedChildren, permission),
    );
  };

  React.useEffect(() => {    
    ((relatedRoomsTerms[0] && !treeData[0]) || needToUpdateTree) && setTreeData([{
      key: 'root',
      title: '',
      style: { display: 'none' },
      children: [
        { key: `create_${dataRoom?.id}`, isLeaf: true, style: { display: 'none' } },
        ...relatedRoomsTerms.map((term) => ({
          key: `${term.related_room!.id}_${term.id}`,
          isLeaf: (canEdit || term.user_status?.status_access === 'approved') ? false : true,
          title: <RelatedRoom
            key={term.id}
            document={term}
            setIsOpen={setModalTermOpen}
            setSelectedTerm={setSelectedTerm}
            setRelatedRoomsTerms={setRelatedRoomsTerms}
            setTreeData={setTreeData}
          />,
          children: term.related_room?.has_childs ? undefined : []
        })),
        { key: `footer_${dataRoom?.id}`, isLeaf: true, style: { display: 'none' } },
      ],
    }])
    setNeedToUpdateTree(false);
  },[relatedRoomsTerms.length, needToUpdateTree]);

  const getTermsOfAccess = async () => {
    setIsLoadingRelatedRooms(true);
    try {
      setLoadedKeys([]);
      setExpandedKeys(['root']);
      const related = await api.getRelatedRooms({room_id: dataRoom?.id!, can_create_access_point: canEdit});
      setRelatedRoomsTerms(related.data.filter((room: IDocument) => room.related_room?.room_status === 'available'));
      setDeletedRoomsTerms(related.data.filter((room: IDocument) => room.related_room?.room_status !== 'available'));
      setNeedToUpdateTree(true);
    } catch (e) {
      messageApi?.error(t('Settings.relatedRooms.accessTermsError'));
    } finally {
      setIsLoadingRelatedRooms(false);
      dispatch(setNeedToUpdateTerms(false));
    }
  };

  const getReferringRooms = async () => {
    try {
      const referringRooms = await api.getReferringRooms(dataRoom?.id!);
      setReferringRooms(referringRooms.data)
    } catch (e) { } 
  };

  React.useEffect(() => {
    dataRoom && getTermsOfAccess();
    dataRoom && getReferringRooms();
  }, [dataRoom?.id]);

  React.useEffect(() => {
    needToUpdateTerms && getTermsOfAccess();
  }, [needToUpdateTerms]);

  const onUploadClick = (inModal?: boolean) => {
    const parentRoomCurrent = inModal ? parentRoom : dataRoom;
    dispatch(setCurrentRelatedRoomTerms(relatedRoomsTerms));
    dispatch(setIsOpenAttachRoomModal({isOpen: true, parentRoom: parentRoomCurrent}));
  };

  const onBeforeUpload = (file: UploadFile) => {   
    const fileExtension = file.name.split('.').reverse()[0];
    
    if (file.name.toLowerCase().replace(`.${fileExtension}`, '').length > 150) {
      messageApi?.error(t('Documents.error.fileNameTooLong'));
      return false;
    };

    if (['pdf', 'docx', 'doc'].includes(fileExtension)) {
      setDocument([file]);
      onUploadClick(true);
    } else {
      setDocument([]);
    };
    return false;
  };

  const acceptedTerms = relatedRoomsTerms.reduce((acc: number, currnetTerm: IDocument) => {
     if (currnetTerm?.user_status?.status_access === 'approved') acc+= 1
    return acc;
  }, 0);

  const relatedRoomsTitle = <div className={classes.titleWrap}>
    <div>{t('Settings.relatedRooms.title')}</div>
    <LinkOutlined className={classes.chain} />
  </div>

  return (
    <>
      {
        referringRooms[0] &&
        <Card className={classes.card} title={t('Settings.relatedRooms.referringRoomTitle')} variant='borderless'>
          <ReferringRooms
            referringRooms={referringRooms}
            setModalTermOpen={setModalTermOpen}
            setSelectedTerm={setSelectedTerm}
          />
        </Card>
      }
      <Card ref={relatedRef} className={classes.card} title={relatedRoomsTitle} variant='borderless'>
        {
          userPermissions?.can_create_access_point &&
            <div onClick={() => onUploadClick(false)}>
              <Dragger
                name='file'
                listType='picture'
                multiple={false}
                maxCount={1}
                showUploadList={false}
                className={classes.dragger}
                openFileDialogOnClick={false}
                beforeUpload={onBeforeUpload}
                accept='.doc,.docx,.xml,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,.pdf'
              >
                <Empty rootClassName={classes.emptyElement} image={Empty.PRESENTED_IMAGE_SIMPLE} description=''/>
                <p className='ant-upload-text'>{t('Settings.terms.uploadFile.fileForConnect')}</p>
                <p className='ant-upload-hint'>{t('Settings.terms.uploadFile.subtitle')}</p>
              </Dragger>
            </div>
        }

        { isLoadingRelatedRooms && <div style={{margin: '25px 0'}}><Spinner size={42} /></div> }
        
        {
          relatedRoomsTerms[0]
          ? <div className={classes.relatedRoomsWrap}>
            <div className={cn(classes.relatedRoomsTitle, canEdit ? classes.canEdit : classes.cantEdit)}>
              <div>
                {`${t('Settings.relatedRooms.relatedRoomName')} (${t('Settings.relatedRooms.access')} ${`${acceptedTerms}/${relatedRoomsTerms?.length}`})`}
              </div>
              <div>
                {t('Settings.relatedRooms.termTitle')}
              </div>
            </div>

            <Tree
              loadData={onLoadData}
              showLine={{ showLeafIcon: ((props: AntTreeNodeProps) => null) }}
              className={classes.treeStyles}
              selectable={false}
              treeData={treeData}
              blockNode={true}
              loadedKeys={loadedKeys}
              switcherIcon={<div className={classes.switcherIconWrap}>
                <CaretDownOutlined />
              </div>}
              switcherLoadingIcon={<Spinner size={14} width={'16px'}/>}
              onExpand={onExpand}
              // autoExpandParent={autoExpandParent}
              expandedKeys={expandedKeys}
            />

          </div>
          : (!isLoadingRelatedRooms &&
            <div className={classes.relatedRoomsWrap}>
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE} 
                className={classes.emptyText}
                description={t('Settings.relatedRooms.noRelatedRooms')}
              />
            </div>
          )
        }

        {
          deletedRoomsTerms[0] && <div className={cn(classes.relatedRoomsWrap, classes.divider)}>
            <div className={cn(classes.relatedRoomsTitle, canEdit ? classes.canEdit : classes.cantEdit)}>
              <div>    
                {t('Settings.relatedRooms.nonActiveTitle')}
              </div>
              <div>
                {t('Settings.relatedRooms.termTitle')}
              </div>
            </div>
            {deletedRoomsTerms.map((term, i) => (
              <RelatedRoom
                key={term.id}
                document={term}
                setIsOpen={setModalTermOpen}
                setSelectedTerm={setSelectedTerm}
                setRelatedRoomsTerms={setRelatedRoomsTerms}
              />
            ))}
          </div>
        }

        {modalTermOpen && (
          <React.Suspense fallback={<div />}>
            <ModalRoomTerm
              open={modalTermOpen}
              setIsOpen={setModalTermOpen}
              selectedTerm={selectedTerm!}
              setSelectedTerm={setSelectedTerm}
              setRelatedRoomsTerms={setRelatedRoomsTerms}
              setTreeData={setTreeData}
              setExpandedKeys={setExpandedKeys}
              setLoadedKeys={setLoadedKeys}
              relatedRoomsTerms={relatedRoomsTerms}
            />
          </React.Suspense>
        )}
        
        {isOpenAttachRoomModal && (
          <React.Suspense fallback={<div />}>
            <ModalRelatedRoom
              isOpen={isOpenAttachRoomModal}
              onBeforeUpload={onBeforeUpload}
              document={document}
              setDocument={setDocument}
              getTermsOfAccess={getTermsOfAccess}
            />
          </React.Suspense>
        )}
      </Card>
    </>
  );
}

export default RelatedRooms;
